import React, { useState } from 'react';
import { Alert, Button, Content, Modal, ModalBody, ModalFooter, ModalHeader } from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import type { Mailbox } from '../../../types/models/Mailbox';

interface MailboxDeleteModalProps {
  mailbox: Mailbox | null;
  onClose: () => void;
  domainName: string;
  mailDomainId: number;
  onSuccess: () => void;
}

function MailboxDeleteModal({ mailbox, onClose, domainName, mailDomainId, onSuccess }: MailboxDeleteModalProps) {
  const apiFetch = useApiFetch();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetAndClose = () => {
    setError(null);
    onClose();
  };

  const handleDelete = async () => {
    if (!mailbox) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const params = new URLSearchParams({ mail_domain_id: String(mailDomainId) });
      const response = await apiFetch(`/emails/mailboxes/${mailbox.id}?${params.toString()}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error((body as { message?: string })?.message ?? `HTTP error! status: ${response.status}`);
      }

      resetAndClose();
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete mailbox');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal variant="small" isOpen={mailbox !== null} onClose={resetAndClose} aria-label="Delete mailbox">
      <ModalHeader title="Delete mailbox" />
      <ModalBody>
        {error && (
          <Alert variant="danger" isInline title="Delete failed" className="pf-v6-u-mb-md">
            {error}
          </Alert>
        )}
        <Content>
          Delete mailbox{' '}
          <strong>
            {mailbox?.user}@{domainName}
          </strong>
          ?
          <br />
          <br />
          The account will be removed immediately — the user can no longer log in and new mail will be rejected.
          Existing messages will be archived for <strong>30 days</strong> and can be recovered during that window by
          contacting the platform operator. After 30 days, the archive is permanently deleted.
        </Content>
      </ModalBody>
      <ModalFooter>
        <Button variant="danger" onClick={handleDelete} isLoading={isSubmitting} isDisabled={isSubmitting}>
          Delete
        </Button>
        <Button variant="link" onClick={resetAndClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default MailboxDeleteModal;
